import { useRef, useState } from "react";
import { useCreateSignature, useDeleteDecaSignature, type SignatureRow } from "../api/hooks";
import { Button, Card, ErrorNotice, Field, Input, Notice, Select, Spinner } from "./ui";
import { formatDateTime, formatHash, signatureMethodLabel, signerRoleLabel } from "../lib/format";
import { SignaturePad, type SignaturePadRef } from "./SignaturePad";

const DELETE_SIG_MAX_AGE_MS = 48 * 60 * 60 * 1000; // 48 h

function isSignatureDeletable(signedAt: string): boolean {
  return Date.now() - new Date(signedAt).getTime() < DELETE_SIG_MAX_AGE_MS;
}

/**
 * Firmas electrónicas del documento.
 *
 * Recado que la norma deja claro y que aquí se escribe tal cual en pantalla:
 * **la firma es opcional**. Su ausencia no bloquea crear, emitir ni descargar
 * un DeCA. Este panel se puede usar exactamente igual sin firmar nada.
 *
 * No se promete firma cualificada eIDAS: la aceptación simple y el trazo en
 * pantalla dejan constancia de quién aceptó y con qué huella del documento,
 * que es lo que guarda `DecaSignature`.
 *
 * Métodos soportados:
 *   - click_to_sign  → aceptación simple, sin canvas
 *   - drawn          → canvas de trazo manuscrito; se bloquea el envío si
 *                      el usuario no ha dibujado nada
 *   - uploaded_certificate → solo deja constancia del método; sin adjunto
 */
export function SignaturePanel({
  documentId,
  signatures,
  isLoading,
  isAdmin = false,
}: {
  documentId: string;
  signatures: SignatureRow[];
  isLoading: boolean;
  isAdmin?: boolean;
}) {
  const [signerName, setSignerName] = useState("");
  const [signerRole, setSignerRole] = useState("CARGADOR");
  const [signatureMethod, setSignatureMethod] = useState("click_to_sign");
  const [drawnEmpty, setDrawnEmpty] = useState(false); // validación visual si intentan enviar sin trazar
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null); // signatureId a borrar
  const [orphanedKeys, setOrphanedKeys] = useState<string[]>([]);
  const padRef = useRef<SignaturePadRef>(null);
  const create = useCreateSignature();
  const deleteSig = useDeleteDecaSignature();

  const isDrawn = signatureMethod === "drawn";

  function handleSubmit() {
    // Si el método es manuscrito, verificar que hay trazo antes de enviar
    if (isDrawn) {
      const dataUrl = padRef.current?.getDataUrl();
      if (!dataUrl) {
        setDrawnEmpty(true);
        return;
      }
      setDrawnEmpty(false);
    }

    create.mutate(
      { id: documentId, signerRole, signerName: signerName.trim(), signatureMethod },
      {
        onSuccess: () => {
          setSignerName("");
          padRef.current?.clear();
          setDrawnEmpty(false);
        },
      },
    );
  }

  return (
    <Card>
      <h2 className="micro-label">Firmas</h2>
      <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
        Firmar es <strong>opcional</strong>: el documento ya es válido y descargable sin ninguna
        firma.
      </p>

      {/* Warning de claves S3 huérfanas tras borrar firma */}
      {orphanedKeys.length > 0 ? (
        <Notice tone="warn">
          <span className="font-semibold">Aviso:</span> La firma se eliminó, pero{" "}
          {orphanedKeys.length === 1 ? "quedó 1 clave" : `quedaron ${orphanedKeys.length} claves`} de
          S3 sin borrar (object lock activo). Se limpiarán cuando el lock expire.
        </Notice>
      ) : null}

      {isLoading ? (
        <Spinner label="Cargando firmas" />
      ) : signatures.length === 0 ? (
        <p className="mt-3 text-sm text-ink-500 dark:text-ink-400">Todavía no hay firmas.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {signatures.map((signature) => {
            const canDelete = isAdmin && isSignatureDeletable(signature.signedAt);
            const isConfirming = deleteConfirm === signature.id;

            return (
              <li key={signature.id} className="rounded-md border border-ink-200 p-3 dark:border-ink-800">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-800 dark:text-ink-100">
                      {signature.signerName}
                    </p>
                    <p className="text-xs text-ink-500 dark:text-ink-400">
                      {signerRoleLabel(signature.signerRole)} · {signatureMethodLabel(signature.signatureMethod)} ·{" "}
                      {formatDateTime(signature.signedAt)}
                    </p>
                    <p className="ident mt-1 break-all text-xs text-ink-500 dark:text-ink-400">
                      huella {formatHash(signature.sha256Hash)}
                    </p>
                  </div>

                  {canDelete && !isConfirming ? (
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setDeleteConfirm(signature.id);
                        setOrphanedKeys([]);
                      }}
                    >
                      Eliminar
                    </Button>
                  ) : null}
                </div>

                {/* Confirmación inline */}
                {isConfirming ? (
                  <div className="mt-3 space-y-2 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
                    <p className="text-xs font-semibold text-red-700 dark:text-red-400">
                      ¿Eliminar esta firma? Esta acción no se puede deshacer.
                    </p>
                    {deleteSig.isError ? <ErrorNotice error={deleteSig.error} /> : null}
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        disabled={deleteSig.isPending}
                        onClick={() => {
                          deleteSig.mutate(
                            { documentId, signatureId: signature.id },
                            {
                              onSuccess: (result) => {
                                setDeleteConfirm(null);
                                if (result?.orphanedKeys && result.orphanedKeys.length > 0) {
                                  setOrphanedKeys(result.orphanedKeys);
                                }
                              },
                            },
                          );
                        }}
                      >
                        {deleteSig.isPending ? "Eliminando…" : "Confirmar borrado"}
                      </Button>
                      <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 space-y-3">
        <Notice tone="neutral">
          La huella SHA-256 se calcula sobre el PDF almacenado en el momento de firmar: sirve para
          demostrar después qué documento exacto se aceptó.
        </Notice>

        <Field label="Firma en nombre de" htmlFor="signer-name">
          <Input
            id="signer-name"
            value={signerName}
            onChange={(event) => setSignerName(event.target.value)}
            placeholder="Nombre y apellidos"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Interviene como" htmlFor="signer-role">
            <Select id="signer-role" value={signerRole} onChange={(event) => setSignerRole(event.target.value)}>
              <option value="CARGADOR">Cargador</option>
              <option value="TRANSPORTISTA">Transportista</option>
              <option value="OPERADOR_INTERMEDIO">Operador intermedio</option>
            </Select>
          </Field>
          <Field label="Forma de firma" htmlFor="signer-method">
            <Select
              id="signer-method"
              value={signatureMethod}
              onChange={(event) => {
                setSignatureMethod(event.target.value);
                setDrawnEmpty(false);
                padRef.current?.clear();
              }}
            >
              <option value="click_to_sign">Aceptación simple</option>
              <option value="drawn">Manuscrita en pantalla</option>
              <option value="uploaded_certificate">Certificado externo</option>
            </Select>
          </Field>
        </div>

        {/* Canvas de firma manuscrita — solo visible cuando se selecciona "drawn" */}
        {isDrawn && (
          <div className="space-y-1">
            <p className="micro-label">Traza tu firma aquí</p>
            <div
              className={`h-40 w-full overflow-hidden rounded-md border bg-white dark:bg-white ${
                drawnEmpty
                  ? "border-red-400 ring-1 ring-red-400"
                  : "border-ink-300 dark:border-ink-600"
              }`}
            >
              <SignaturePad ref={padRef} />
            </div>
            {drawnEmpty && (
              <p className="text-xs text-red-600 dark:text-red-400">
                Debes trazar tu firma antes de registrarla.
              </p>
            )}
            <Button
              variant="quiet"
              onClick={() => {
                padRef.current?.clear();
                setDrawnEmpty(false);
              }}
            >
              Borrar trazo
            </Button>
          </div>
        )}

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={create.isPending || signerName.trim().length === 0}
          onClick={handleSubmit}
        >
          {create.isPending ? "Firmando…" : "Registrar firma"}
        </Button>
      </div>
    </Card>
  );
}
